// A copy of the admin's useAutoRefresh.js (src/Organizer/ManageOireachtas/ in the
// admin repo; code reviews 86bb9e53 / ac80e4a0) — keep them in step.
import { useEffect, useRef, useState } from "react";

// Re-reads a screen by itself while it is on show (Devin 2026-11-02, after the
// ECRO dress rehearsal): other tabulators save, sign off and publish from
// other laptops, and the screen should catch up without the browser's refresh.
//
// run() is called every `everyMs` while the browser tab is visible, `active`
// is true and `paused` is false, and right away when the tab comes back into
// view (unless the last run finished under 5 seconds ago). A run still waiting
// for its answer is never overlapped by the next tick, so a read slower than
// the interval still lands (plan review 7631d72b R1-F3).
//
// run() may answer false when it chose to skip (its own reads still busy).
//
// Returns { lastAt, failed }: when the last run succeeded, and whether the
// latest one failed (for the screen's quiet "Updated …" line; no toast).
export const RETURN_GAP_MS = 5000;

export default function useAutoRefresh(run, { everyMs, active = true, paused = false }) {
  const runRef = useRef(run);
  runRef.current = run;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const inFlight = useRef(false);
  const lastAtRef = useRef(null);
  const [lastAt, setLastAt] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!active) return undefined;
    let live = true;
    const tick = async () => {
      // a run waits for its answer however long it takes: releasing it early
      // would let the next run supersede it, again and again on a slow
      // connection (code review 86bb9e53 R2). A hung read is failed by the
      // browser in the end; meanwhile the "Updated" time stops moving.
      if (inFlight.current || pausedRef.current || document.visibilityState !== "visible") return;
      inFlight.current = true;
      try {
        // run() answering false skipped this time: not a refresh
        const done = await runRef.current();
        if (live && done !== false) {
          setFailed(false);
          setLastAt(new Date());
        }
      } catch (e) {
        if (live) setFailed(true);
      } finally {
        inFlight.current = false;
        lastAtRef.current = new Date();
      }
    };
    const timer = setInterval(tick, everyMs);
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const age = lastAtRef.current ? Date.now() - lastAtRef.current.getTime() : Infinity;
      if (age > RETURN_GAP_MS) tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      live = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [active, everyMs]);

  return { lastAt, failed };
}
